import * as React from 'react';
import { SubscriberService, OnUpdateEvent, createStaticServiceInstance, createUseZenAutoArtService } from "./base-subscriber-service";
import { DialogWrapper } from '@/components';
import { BLAZOR_ASSEMBLY } from "@/common/constants";
import { elementToJson } from '@/common/utilities';
import ShortcutManager from '@/editor/ShortcutManager';

const WINDOW_EVENTS_TO_FORWARD = {
    "zaa-editor-ready": null,
    "editor-mode-changed": null,
    "editor-units-changed": null,
    "history-changed": null,
    "svgcontent-changed": null,
    "svgcontent-loaded-from-computer": null,
    "selection-changed": null,
    "element-state-changed": null,
    "zoom-complete": null,
    "contextset": "isolation-context-changed",
    "layers-context-menu-close": null,
};

/**
 * Document events Blazor can ask to receive, mapped to the name they are forwarded under. Unlike the window events
 * above, these are opt-in; see `BlazorService.requestDocumentEventForwarding`.
 */
const DOCUMENT_EVENTS_TO_FORWARD = {
    "mousedown": "document-mousedown",
    "mousemove": "document-mousemove",
    "mouseup": "document-mouseup",
    "click": "document-click",
};

export default class BlazorService extends SubscriberService<BlazorService> {
    public static Get(comp: React.Component, onUpdate?: OnUpdateEvent) {
        return blazorServiceInstance._RegisterComponent(comp, onUpdate);
    }

    public DotNetServiceRef: DotNet.DotNetObject = null;

    /**
     * Document events are only forwarded while something in Blazor has asked for them, keyed by the forwarded event
     * name (e.g. "document-click"). Forwarding every click and mouse button press regardless meant an interop call,
     * element serialization and a .NET notification fan-out on every click anywhere in the page.
     */
    private _documentEventForwardingCounts: Record<string, number> = {};
    private _pendingMouseMoveFrame: number | null = null;
    private _pendingMouseMoveEvent: MouseEvent | null = null;

    public Initialize(dotNetServiceRef: DotNet.DotNetObject) {
        this.DotNetServiceRef = dotNetServiceRef;

        for (const [eventName, altName] of Object.entries(WINDOW_EVENTS_TO_FORWARD)) {
            window.addEventListener(eventName, ev => { this.DispatchEventToBlazor(ev, altName ?? eventName) });
        }

        for (const [eventName, altName] of Object.entries(DOCUMENT_EVENTS_TO_FORWARD)) {
            document.addEventListener(eventName, ev => {
                // Only forwarded while at least one subscriber has opted in.
                if (this.isDocumentEventForwarded(altName)) {
                    this.DispatchEventToBlazor(ev, altName);
                }
            });
        }

        console.log("Blazor Service - Service is live");
    }

    public PushGlobalLoadingState(loadingStateId: string, label?: string) {
        DotNet.invokeMethod(BLAZOR_ASSEMBLY, 'PushGlobalLoadingState', loadingStateId, label ?? null);
    }

    public PopGlobalLoadingState(loadingStateId: string) {
        DotNet.invokeMethod(BLAZOR_ASSEMBLY, 'PopGlobalLoadingState', loadingStateId);
    }

    /**
     * Requests that the given document events be forwarded to Blazor.
     * Each call must be paired with a corresponding `releaseDocumentEventForwarding` call with the same names.
     * @param forwardedEventNames Forwarded event names, e.g. "document-click" (see DOCUMENT_EVENTS_TO_FORWARD).
     */
    public requestDocumentEventForwarding(...forwardedEventNames: string[]): void {
        for (const name of forwardedEventNames) {
            if (!Object.values(DOCUMENT_EVENTS_TO_FORWARD).includes(name)) {
                console.warn(`Blazor Service - '${name}' is not a forwardable document event.`);
                continue;
            }

            this._documentEventForwardingCounts[name] = (this._documentEventForwardingCounts[name] ?? 0) + 1;
        }
    }

    /**
     * Releases document event forwarding previously requested with `requestDocumentEventForwarding`.
     * @param forwardedEventNames Forwarded event names, e.g. "document-click" (see DOCUMENT_EVENTS_TO_FORWARD).
     */
    public releaseDocumentEventForwarding(...forwardedEventNames: string[]): void {
        for (const name of forwardedEventNames) {
            this._documentEventForwardingCounts[name] = Math.max(0, (this._documentEventForwardingCounts[name] ?? 0) - 1);
        }

        if (!this.isDocumentEventForwarded(DOCUMENT_EVENTS_TO_FORWARD.mousemove) && this._pendingMouseMoveFrame !== null) {
            cancelAnimationFrame(this._pendingMouseMoveFrame);
            this._pendingMouseMoveFrame = null;
            this._pendingMouseMoveEvent = null;
        }
    }

    private isDocumentEventForwarded(forwardedEventName: string): boolean {
        return (this._documentEventForwardingCounts[forwardedEventName] ?? 0) > 0;
    }

    private DispatchEventToBlazor(ev: Event, alternateName?: string) {
        // mousemove is throttled to one interop call per animation frame.
        // movementX/Y deltas are summed across coalesced events so no movement is lost;
        // absolute coords (clientX/Y etc.) use the latest event's values.
        if (ev.type === 'mousemove') {
            const mouseEv = ev as MouseEvent;
            // Synthesize the pending event immediately, accumulating movement deltas into it.
            // Note: `target` and `offsetX/Y` will be null/0 as they cannot be set on a synthetic event.
            this._pendingMouseMoveEvent = new MouseEvent(mouseEv.type, {
                movementX: (this._pendingMouseMoveEvent?.movementX ?? 0) + mouseEv.movementX,
                movementY: (this._pendingMouseMoveEvent?.movementY ?? 0) + mouseEv.movementY,
                clientX: mouseEv.clientX,
                clientY: mouseEv.clientY,
                screenX: mouseEv.screenX,
                screenY: mouseEv.screenY,
                button: mouseEv.button,
                buttons: mouseEv.buttons,
                bubbles: mouseEv.bubbles,
                cancelable: mouseEv.cancelable,
            });
            if (this._pendingMouseMoveFrame === null) {
                this._pendingMouseMoveFrame = requestAnimationFrame(() => {
                    this._pendingMouseMoveFrame = null;
                    const pendingEv = this._pendingMouseMoveEvent;
                    this._pendingMouseMoveEvent = null;
                    if (pendingEv) {
                        this._dispatchEventToBlazorInternal(pendingEv, alternateName);
                    }
                });
            }
            return;
        }

        this._dispatchEventToBlazorInternal(ev, alternateName);
    }

    private _dispatchEventToBlazorInternal(ev: Event, alternateName?: string) {
        let detail = null;
        if (ev instanceof CustomEvent) {
            detail = ev.type === "contextset"
                ? this._serializeIsolationContext(ev.detail instanceof Element ? ev.detail : null)
                : ev.detail;

            if (detail instanceof Element) {
                // Only svgcontent-changed carries a single element, and it is the document root. Serializing it sent the
                // entire document (every attribute, embedded images, and its text repeated at every nesting level) on
                // every document load, and nothing in .NET reads it. Blazor owns the document; this is just a signal.
                detail = null;
            } else if (Array.isArray(detail)) {
                // selection-changed and element-state-changed: fired on every selection and edit.
                detail = detail.map(e => e instanceof Element ? elementToJson(e, 0, false) : e);
            }
        } else if (ev instanceof MouseEvent) {
            detail = {
                clientX: ev.clientX,
                clientY: ev.clientY,
                screenX: ev.screenX,
                screenY: ev.screenY,
                offsetX: ev.offsetX,
                offsetY: ev.offsetY,
                button: ev.button,
                buttons: ev.buttons,
                movementX: ev.movementX,
                movementY: ev.movementY,
                target: ev.target instanceof Element ? elementToJson(ev.target, 0, false) : null
            };
        } else if (ev instanceof KeyboardEvent) {
            detail = {
                ...ev, // TODO: This doesn't seem to work for events?
                target: ev.target instanceof Element ? elementToJson(ev.target, 0, false) : null
            };
        }

        return this.DotNetServiceRef.invokeMethodAsync('OnJsEvent', alternateName ?? ev.type, detail);
    }

    private _serializeIsolationContext(context: Element | null) {
        const contextStack = [];
        let element = context;

        while (element && element.id !== "svgcontent") {
            const titleElement = Array.from(element.children)
                .find(child => child.tagName.toLowerCase() === "title");

            contextStack.push({
                title: titleElement?.innerHTML ?? element.id ?? "<Group>",
            });
            element = element.parentElement;
        }

        return contextStack.reverse();
    }

    public setIsolationContext(contextIndex: number | null) {
        const canvas = window["_zaa_services"].svgEditService.canvas;
        const contextStack: Element[] = [];
        let element = canvas.getCurrentGroup() as Element | null;

        while (element && element.id !== "svgcontent") {
            contextStack.push(element);
            element = element.parentElement;
        }

        const context = contextIndex === null ? null : contextStack.reverse()[contextIndex];
        if (!context || context.classList.contains("layer")) {
            canvas.leaveContext();
        } else {
            canvas.setContext(context);
        }
        canvas.clearSelection();
    }

    // Events called from Blazor

    public dispatchEvent(eventName: string, data: any) {
        const ev = new CustomEvent(eventName, { detail: data });
        window.dispatchEvent(ev);
    }

    public getViewportSize() {
        return {
            width: window.innerWidth,
            height: window.innerHeight,
        };
    }

    public openDialog(dialogName: string, args?: any[]) {
        const dialog = DialogWrapper.GetDialog(dialogName);
        if (!dialog) {
            console.error(`Dialog with ID '${dialogName}' not found.`);
            return;
        }

        if (typeof dialog.openDialog !== "function") {
            console.error(`Element with ID '${dialogName}' is not a valid dialog.`);
            return;
        }

        dialog.openDialog(...args);
    }

    public openToolDialog(dialogName: string, relativeToElementID?: string) {
        const relativeEl = relativeToElementID ? document.getElementById(relativeToElementID) : null;
        const dialog = DialogWrapper.GetDialog(dialogName);
        if (!dialog) {
            console.error(`Dialog with ID '${dialogName}' not found.`);
            return;
        }

        if (typeof dialog.openToolDialog !== "function") {
            console.error(`Element with ID '${dialogName}' is not a valid tool dialog.`);
            return;
        }

        dialog.openToolDialog(relativeEl);
    }

    public registerShortcut(shortcut: string, alias: string, name: string, category: string, description: string) {
       ShortcutManager.RegisterShortcut(shortcut, 
           () => {
               DotNet.invokeMethodAsync(BLAZOR_ASSEMBLY, 'OnJsEvent', "shortcut-triggered", shortcut);
           }, {
            actionName: name,
            actionCategory: category,
            actionDescription: description,
            alias: alias
        });
    }

    public invokeShortcut(shortcutAlias: string) {
        const shortcut = ShortcutManager.GetShortcutFromAlias(shortcutAlias);
        if (shortcut) {
            shortcut.action(new Event("blazor-invoked"));
        } else {
            console.warn(`Shortcut with alias ${shortcutAlias} not found.`);
        }
    }
   
}

export const blazorServiceInstance = createStaticServiceInstance(new BlazorService(), "blazorService");

// For React Functional Components
export const useBlazorService = createUseZenAutoArtService(blazorServiceInstance);
